import { css } from 'styled-components';

const variables = css`
  /* =====================
     Dark Theme (default)
     ===================== */
  :root {
    /* Backgrounds */
    --dark-navy: #0f141a; /* softer than pure black */
    --navy: #1b222b; /* navbar + main sections */
    --light-navy: #242d3c; /* cards, highlights */
    --lightest-navy: #2e3a4b; /* borders, dividers */
    --navy-shadow: rgba(0, 0, 0, 0.6);

    /* Text */
    --dark-slate: #8a94a6; /* muted text */
    --slate: #c0c7d3; /* body text */
    --light-slate: #e0e6f0; /* strong text */
    --lightest-slate: #f5f7fa; /* headings */
    --white: #ffffff;

    /* Accents */
    --green: #3ddc97; /* fresh green */
    --green-tint: rgba(61, 220, 151, 0.15);
    --pink: #f472b6; /* soft pink */
    --blue: #58a6ff; /* bright interactive blue */
    --purple: #a78bfa; /* gentle purple */

    /* Components */
    --card-bg: var(--light-navy);
    --card-border: var(--lightest-navy);
    --hover-bg: rgba(255, 255, 255, 0.05);
    --focus-ring: rgba(88, 166, 255, 0.4);

    /* Hover/focus effects */
    --card-hover: rgba(88, 166, 255, 0.1);
    --link-hover-bg: rgba(88, 166, 255, 0.05);

    /* Navigation */
    --navBackground: rgba(27, 34, 43, 0.85);
    --navBackgroundScrolled: rgba(27, 34, 43, 0.95);
    --navBorder: var(--lightest-navy);
    --shadow: var(--navy-shadow);

    --text: var(--slate);
    --heading: var(--lightest-slate);
    --background: var(--navy);

    /* Nav Links */
    --nav-link: #ffffff;
    --nav-link-hover: #20c997;
    --nav-link-active: #20c997;
  }

  /* =====================
     Light Theme
     ===================== */
  [data-theme='light'] {
    /* Backgrounds */
    --dark-navy: #f0f2f5; /* light gray background */
    --navy: #ffffff; /* navbar + main sections */
    --light-navy: #f8f9fa; /* cards, highlights */
    --lightest-navy: #e9ecef; /* borders, dividers */
    --navy-shadow: rgba(0, 0, 0, 0.05);

    /* Text */
    --dark-slate: #6c757d; /* muted text */
    --slate: #495057; /* body text */
    --light-slate: #343a40; /* strong text */
    --lightest-slate: #212529; /* headings */
    --white: #ffffff;

    /* Accents */
    --green: #20c997; /* modern mint green */
    --green-tint: rgba(32, 201, 151, 0.1);
    --pink: #ff6b6b; /* vibrant coral */
    --blue: #4dabf7; /* soft, modern blue */
    --purple: #9b5de5; /* fresh purple */

    /* Components */
    --card-bg: #ffffff;
    --card-border: #dee2e6; /* subtle border */
    --hover-bg: #f1f3f5; /* subtle hover effect */
    --focus-ring: rgba(77, 171, 247, 0.3);

    /* Hover/focus effects */
    --card-hover: rgba(77, 171, 247, 0.08);
    --link-hover-bg: rgba(32, 201, 151, 0.1);

    /* Navigation */
    --navBackground: rgba(255, 255, 255, 0.95);
    --navBackgroundScrolled: rgba(255, 255, 255, 1);
    --navBorder: #dee2e6;
    --shadow: rgba(0, 0, 0, 0.05);

    --text: var(--slate);
    --heading: var(--lightest-slate);
    --background: var(--dark-navy);

    /* Nav Links */
    --nav-link: #495057;
    --nav-link-hover: #20c997;
    --nav-link-active: #20c997;
  }

  /* =====================
     Common Variables
     ===================== */
  :root {
    /* Fonts */
    --font-sans:
      'Calibre', 'Inter', 'San Francisco', 'SF Pro Text', -apple-system, system-ui, sans-serif;
    --font-mono: 'SF Mono', 'Fira Code', 'Fira Mono', 'Roboto Mono', monospace;

    /* Font Sizes */
    --fz-xxs: 12px;
    --fz-xs: 13px;
    --fz-sm: 14px;
    --fz-md: 16px;
    --fz-lg: 18px;
    --fz-xl: 20px;
    --fz-xxl: 22px;
    --fz-heading: 32px;

    /* Layout */
    --border-radius: 10px;
    --nav-height: 100px;
    --nav-scroll-height: 70px;
    --tab-height: 42px;
    --tab-width: 200px;

    /* Animations */
    --easing: cubic-bezier(0.645, 0.045, 0.355, 1);
    --transition: all 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);

    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

    --hamburger-width: 30px;
  }
`;

export default variables;
